import { encodeBaseN } from '@cf-shared/shared-util';
import { Capacitor } from '@capacitor/core';
import { isSafariAppUserAgent } from 'util/safari-user-agent';

// This module must NOT import environments/environment. The Firebase config
// files import getRootDomain and resolveFirebaseAuthDomain from here, and
// environments/environment imports those config files, so such an import closes
// an import cycle whose boot order depends on the bundler.
// environment-helpers-imports.test.ts fails if it comes back. Code that needs
// AppEnvironment — the backend origins — lives in util/backend-origin.ts.

export function isCapacitorNative(): boolean {
  return Capacitor.isNativePlatform();
}

export function getRootDomain() {
  return 'mh-physique.app';
}

// Firebase's OAuth handler (signInWithPopup) runs on the configured authDomain.
// On mobile web (iOS Safari + Android browsers) the branded *custom* authDomain breaks:
// WebKit/Chromium storage partitioning makes the auth-handler relay iframe a third-party
// context, so Firebase's sessionStorage isn't readable on return (auth/missing-initial-state),
// and the return navigation re-enters the app's claimed App/Universal Link and hands off to
// the installed native app. The default `<projectId>.firebaseapp.com` domain serves Google's
// Storage-Access-API helper and is never claimed by the app, so it works on mobile browsers.
// Keep the branded custom domain on desktop web (popup flow reliable there) and native
// (which uses the SocialLogin plugin and never reads authDomain).
export function resolveFirebaseAuthDomain(customAuthDomain: string, projectId: string): string {
  // if mobile web (iOS Safari or an Android browser, not the Capacitor native shell),
  // use the firebaseapp.com default
  if ((isPlatformIOS() && !isCapacitorNative()) || isAndroidBrowser()) {
    return `${projectId}.firebaseapp.com`;
  }
  return customAuthDomain;
}

export function getSupportEmail() {
  return `support@${getRootDomain()}`;
}

export function getNoReplyEmail() {
  return `noreply@${getRootDomain()}`;
}
export function isLocalEnv() {
  // on Capacitor native, hostname is always 'localhost' — but a native build is never "local dev"
  if (isCapacitorNative()) return false;
  return location.hostname.startsWith('local');
}
export function isCanaryEnv() {
  return location.hostname.startsWith('canary');
}
export function isPlatformIOS() {
  return [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod'
  ].includes(navigator.platform)
    // iPad on iOS 13 detection
    || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
}

export function isPlatformAndroid() {
  return Capacitor.getPlatform() === 'android';
}

// Capacitor's getPlatform() is typed `string`; on every build it returns one
// of these three. Centralizes the narrowing cast so callers don't repeat it.
export function getCapacitorPlatform(): 'android' | 'ios' | 'web' {
  return Capacitor.getPlatform() as 'android' | 'ios' | 'web';
}

// True when running in a browser on an Android device (NOT the Capacitor-Android
// native shell). UA-based on purpose — works in Chrome DevTools device
// emulation, used by AppInstallService to drive the mobile-web install nudge.
// Use isPlatformAndroid() for Capacitor-native gating, isAndroidBrowser() for
// platform-aware browser UX.
export function isAndroidBrowser(): boolean {
  if (isCapacitorNative()) return false;
  return /Android/i.test(navigator.userAgent);
}

export function isBrowserSafari() {
  // Safari UA contains "Safari" but not "Chrome" or "Chromium" (Chrome-based browsers include "Safari" too)
  const ua = navigator.userAgent;
  return ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium');
}

// True when the browser APPLICATION is Safari itself, as opposed to another
// browser built on Apple's WebKit engine. The rule lives in
// util/safari-user-agent so it can be unit-tested with no browser; this reads
// the two things only a browser can answer. NOT the same question as
// isBrowserSafari() above — see that module's header.
export function isSafariBrowserApp(): boolean {
  return isSafariAppUserAgent(navigator.userAgent, isPlatformIOS());
}

export function isAppStandalone() {
  return isCapacitorNative() ||
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator['standalone'] === true; // For iOS standalone check
}

export const smallScreenMaxWidthBase: number = 800;

let smallScreenMaxWidth = smallScreenMaxWidthBase;

export function setSmallScreenMaxSize(maxWidth: number) {
  smallScreenMaxWidth = maxWidth;
}

export function hasClientSmallScreen(): boolean {
  return document.documentElement.clientWidth < smallScreenMaxWidth;
}

// coarse touch-device heuristic (same check used by cs-input-time to decide
// whether to open the project's custom virtual keyboard)
export function hasTouchSupport(): boolean {
  return ('ontouchstart' in document.documentElement);
}

export function getSessionId() {
  const dec = '0123456789';

  let tabID = undefined;
  if (sessionStorage.tabID) {
    tabID = sessionStorage.tabID;
  } else {
    // if there is a session id in the url, use that
    const url = new URL(window.location.href);
    if (url.searchParams.has('utm_clid')) {
      tabID = url.searchParams.get('utm_clid');
      // if not found anywhere, generate a new one
    } else {
      tabID = encodeBaseN(Uint8Array.from(Math.random().toString(16).substring(2)), dec);
    }
    sessionStorage.tabID = tabID;
  }
  return tabID;
}

export function getDeviceId(): string {
  const dec = '0123456789';
  if (!localStorage.deviceId) {
    localStorage.deviceId = encodeBaseN(Uint8Array.from(Math.random().toString(16).substring(2)), dec);
  }
  return localStorage.deviceId;
}

// check whether running in E2E test environment
export function isE2ETest(): boolean {
  // Check sessionStorage (persists across page reloads/navigation)
  try {
    return sessionStorage.getItem('__CF_E2E_TEST__') === 'true';
  } catch (e) {
    // sessionStorage might not be accessible in some contexts
    console.warn('isE2ETest: sessionStorage access error', e);
    return false;
  }
}

/**
 * The browser's localStorage, or null where there is not one (#4401).
 *
 * There is a real "not one" case and it is not only server-side rendering: this same
 * code is loaded under plain node by the unit tests, and a browser can refuse the whole
 * API outright in a private window or with site data blocked. Callers treat null as "I
 * cannot remember anything", which every one of them is written to survive.
 */
export function browserLocalStorageOrNull(): Storage | null {
  return typeof localStorage !== 'undefined' ? localStorage : null;
}

// `probeSpeaksForLocalStore()` used to live here. It moved to
// `services/local-database-engine.ts` in #4981 phase 2, because it now asks the
// engine question rather than repeating a platform check, and this module imports
// nothing from `services/` — calling it from here would create an import cycle.
